// Cliente web experimental para consumir la API XR (sesiones + telemetría).
// NO es SDK oficial de Zapworks. No persistir tokens ni usar esto para premios.
// API viva de referencia: https://experience.untitledps.com (solo backend).

/** @typedef {'experience_opened'|'tracking_acquired'|'tracking_lost'|'game_started'|'game_finished'|'share_clicked'|'cta_clicked'} CanonicalEventType */

/** Eventos canónicos aceptados por POST /api/v1/events */
export const CANONICAL_EVENTS = Object.freeze([
  'experience_opened',
  'tracking_acquired',
  'tracking_lost',
  'game_started',
  'game_finished',
  'share_clicked',
  'cta_clicked',
]);

/** Metadatos permitidos (strings ≤80, máx. 6 claves) */
export const ALLOWED_METADATA_KEYS = Object.freeze([
  'scene_id',
  'target_id',
  'variant',
  'duration_ms',
  'device_type',
  'result',
]);

const DEFAULT_API_BASE = 'https://experience.untitledps.com';

function assertCampaign(campaign) {
  if (typeof campaign !== 'string' || !/^[a-z0-9][a-z0-9-]*$/.test(campaign) || campaign.length > 64) {
    throw new Error('campaign inválida: usa slug [a-z0-9-] (ej. demo-xr)');
  }
}

function assertEventType(eventType) {
  if (!CANONICAL_EVENTS.includes(eventType)) {
    throw new Error(`event_type no canónico: ${eventType}. Permitidos: ${CANONICAL_EVENTS.join(', ')}`);
  }
}

function assertMetadata(metadata) {
  const keys = Object.keys(metadata);
  if (keys.length > 6) throw new Error('metadata: máximo 6 claves');
  for (const key of keys) {
    if (!ALLOWED_METADATA_KEYS.includes(key)) {
      throw new Error(`metadata clave no admitida: ${key}`);
    }
    const value = metadata[key];
    if (typeof value === 'string' && value.length > 80) {
      throw new Error(`metadata ${key}: string demasiado largo`);
    }
  }
}

function newIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `idemp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export class XrBackendClient {
  /**
   * @param {{ apiBase?: string, campaign: string }} options
   */
  constructor({ apiBase = DEFAULT_API_BASE, campaign }) {
    assertCampaign(campaign);
    this.apiBase = String(apiBase).replace(/\/$/, '');
    this.campaign = campaign;
    this.token = null;
    this.sessionId = null;
  }

  async start() {
    const r = await fetch(`${this.apiBase}/api/v1/sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ campaign: this.campaign }),
    });
    if (!r.ok) throw new Error(`No se pudo iniciar sesión: ${r.status}`);
    const data = await r.json();
    this.token = data.token;
    this.sessionId = data.session_id;
    return { sessionId: data.session_id, expiresAt: data.expires_at };
  }

  /**
   * @param {CanonicalEventType} event_type
   * @param {Record<string, string|number|boolean>} [metadata]
   */
  async track(event_type, metadata = {}) {
    if (!this.token) throw new Error('Primero ejecutar start()');
    assertEventType(event_type);
    assertMetadata(metadata);
    const idempotencyKey = newIdempotencyKey();
    const r = await fetch(`${this.apiBase}/api/v1/events`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.token}`,
        'Idempotency-Key': idempotencyKey,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ event_type, metadata }),
    });
    if (!r.ok) throw new Error(`Evento no registrado: ${r.status}`);
    return r.json();
  }

  /** Descarta el token de memoria (no invalida la sesión en servidor). */
  clear() {
    this.token = null;
    this.sessionId = null;
  }
}

/**
 * Factory corta para Mattercraft / Universal AR / scripts embebibles.
 * @param {{ apiBase?: string, campaign: string }} options
 */
export function createXrBackendClient(options) {
  return new XrBackendClient(options);
}
